import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCertificate,
  faSquarePlus
} from '@fortawesome/free-solid-svg-icons';
import TitleBadge from './TitleBadge';

const Widget = ({ id, children, className = '', onClick, title, subtitle, icon, link, backgroundImage, hidden = false, isNew = false, links = {}, date, verified = false, slug, titled = false, chessTitle = '' }) => {
  const navigate = useNavigate();
  const [showVerifiedOverlay, setShowVerifiedOverlay] = useState(false);
  const hasBackgroundImage = backgroundImage || (typeof icon === 'object' && icon?.props?.src);
  const backgroundImageUrl = backgroundImage || (typeof icon === 'object' ? icon.props.src : null);
  
  // Check if widget has an internal page
  const hasInternalPage = title === '@TrendingChess' || title === 'TrendingChess' || title === 'Charlize' || title === 'Charlize van Zyl' || title === 'Christopher' || title === 'Christopher Poloniato';
  const getInternalPagePath = () => {
    if (title === '@TrendingChess' || title === 'TrendingChess') return '/TrendingChess';
    if (title === 'Charlize' || title === 'Charlize van Zyl') return '/Charlize';
    if (title === 'Christopher' || title === 'Christopher Poloniato') return '/Christopher';
    return null;
  };
  const internalPagePath = hasInternalPage ? getInternalPagePath() : null;
  
  if (hidden) return null;
  
  return (
    <div className="flex flex-col">
      {/* Widget Container with relative positioning */}
      <div className="relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className={`
            ${hasBackgroundImage ? 'bg-cover bg-center bg-no-repeat' : 'bg-gray-800'}
            ${hasBackgroundImage ? 'border-4 border-gray-600' : 'border border-gray-700'}
            rounded-3xl 
            ${hasBackgroundImage ? 'p-0' : 'p-6'}
            w-full
            aspect-square
            flex 
            items-center 
            justify-center
            cursor-pointer
            ${hasBackgroundImage ? 'hover:border-gray-500' : 'hover:border-gray-600'}
            hover:scale-105
            transition-all
            duration-200
            shadow-lg
            relative
            overflow-hidden
            ${className}
          `}
          style={hasBackgroundImage ? { 
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundClip: 'padding-box'
          } : {}}
          onClick={onClick || (slug ? () => navigate(`/${slug}`) : (link ? () => window.open(link, '_blank') : undefined))}
        >
        {/* NEW Ribbon Banner */}
        {isNew && (
          <div 
            className="absolute text-white font-bold shadow-lg z-10"
            style={{
              top: '8px',
              right: '-22px',
              transform: 'rotate(45deg)',
              transformOrigin: 'center',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              width: '75px',
              textAlign: 'center',
              padding: '3px 0',
              fontSize: '9px'
            }}
          >
            NEW
          </div>
        )}

        {/* Verified Badge - Top Left Corner */}
        {/* {verified && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowVerifiedOverlay(true);
              setTimeout(() => setShowVerifiedOverlay(false), 2000);
            }}
            className="absolute top-1 left-1 text-green-500  z-10 "
            title="Verified"

            style={{ border: '1px solid black', borderRadius: '5px' }}
        
          >
            <FontAwesomeIcon icon={faCertificate} className="text-sm" />
          </button>
        )} */}
        
        {children || (
          <div className="flex flex-col h-full relative align-center justify-center">
            {!hasBackgroundImage && (
              <div className="flex-1 flex items-center justify-center w-full h-full">
                <div className="text-gray-400 text-4xl flex items-center justify-center">
                  {typeof icon === 'string' && icon === 'FontAwesomeIcon' ? (
                    <FontAwesomeIcon icon={faSquarePlus} />
                  ) : (
                    typeof icon === 'string' ? icon : (icon || '□')
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        </motion.div>
        
        {/* Verified Popup - Centered at bottom of image container */}
        {showVerifiedOverlay && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20 pointer-events-none">
            <div className="bg-gray-900 text-white text-xs px-3 py-1 rounded shadow-lg border border-green-500 whitespace-nowrap">
              Verified
            </div>
          </div>
        )}
      </div>
      
      {/* Spacing row — social icons moved to detail pages */}
      <div className="flex justify-center items-center gap-2 mt-2 mb-1 min-h-[20px]">
        {date && (
          <div className="text-xs text-gray-400 font-medium">
            {date}
          </div>
        )}
      </div>
      
      {/* Title and Subtitle Below */}
      <div className="text-center mt-1">
        <div className="text-xs font-bold text-gray-100">
          <TitleBadge titled={titled} chessTitle={chessTitle} size="sm" />{title || `Widget ${id}`}
        </div>
        {subtitle && (
          <div className="text-xs leading-tight mt-0.5 text-gray-400">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

export default Widget;